import React from 'react';
import NotificationBell from 'assets/images/svgs/notificationBell.svg';
import { UnmuteNotification, Sharing } from '../iconComponents';

export type DocumentCardVariant = 'owned' | 'shared-to-me' | 'shared-by-me';

export interface DocumentCardProps {
  id: number;
  name: string;
  renewalDate?: string | null;
  hasReminder?: boolean;
  sharesCount?: number;
  isSharedViaShareAll?: boolean; // True if user has Share All enabled with any connection
  isSharedViaParent?: boolean; // True if doc is shared because parent vehicle/property is shared
  parentSharesCount?: number; // Number of people the parent vehicle/property is shared with
  // For shared documents
  variant?: DocumentCardVariant;
  ownerName?: string;
  canEdit?: boolean;
  shareType?: 'explicit' | 'share_all'; // How this doc was shared to me
  // Actions
  onClick?: () => void;
  onSharingIconClick?: () => void; // Called when sharing icon is clicked
}

const DocumentCard: React.FC<DocumentCardProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  id,
  name,
  renewalDate,
  hasReminder = false,
  sharesCount = 0,
  isSharedViaShareAll = false,
  isSharedViaParent = false,
  parentSharesCount = 0,
  variant = 'owned',
  ownerName,
  canEdit,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  shareType,
  onClick,
  onSharingIconClick,
}) => {
  const isSharedToMe = variant === 'shared-to-me';
  // Show sharing icon if explicitly shared OR if Share All is enabled OR if parent vehicle/property is shared
  const isSharedWithOthers = !isSharedToMe && (sharesCount > 0 || isSharedViaShareAll || isSharedViaParent);

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const ownerDisplay = ownerName || 'Unknown';
  const renewalDisplay = formatDate(renewalDate);

  // Determine sharing icon color: purple if shared to me, green if shared with others
  const getSharingIconColor = () => {
    if (isSharedToMe) return '#744DA1';
    if (isSharedWithOthers) return '#059669';
    return null;
  };
  const sharingIconColor = getSharingIconColor();

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      role="button"
      tabIndex={0}
      className="transition-all duration-200 hover:scale-[1.01] hover:shadow-lg"
      style={{
        background: 'white',
        borderRadius: 16,
        padding: '14px 16px',
        marginTop: 12,
        boxShadow: '0 2px 8px rgba(57, 38, 79, 0.08)',
        cursor: 'pointer',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        {/* Left: Bell Icon for reminders */}
        <div
          style={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {hasReminder ? (
            <img
              src={NotificationBell}
              style={{ width: 20, height: 20 }}
              alt="reminder"
            />
          ) : (
            <div style={{ width: 20, height: 20, color: '#D1D5DB' }}>
              <UnmuteNotification />
            </div>
          )}
        </div>

        {/* Center: Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Document Name */}
          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              color: '#1F2937',
              lineHeight: 1.4,
              wordBreak: 'break-word',
            }}
          >
            {name}
          </div>

          {/* Subtitle row(s) */}
          {isSharedToMe ? (
            <>
              {/* Renewal date on its own line (if exists) */}
              {renewalDate && (
                <div
                  style={{
                    fontSize: 13,
                    color: '#6B7280',
                    marginTop: 2,
                  }}
                >
                  {`Renewal: ${renewalDisplay}`}
                </div>
              )}
              {/* From owner + permission badge */}
              <div
                style={{
                  fontSize: 13,
                  color: '#6B7280',
                  marginTop: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <span>
                  {'From '}
                  <span style={{ fontWeight: 500 }}>{ownerDisplay}</span>
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: canEdit ? '#059669' : '#744DA1',
                    background: canEdit ? '#ECFDF5' : '#F3E8FF',
                    padding: '2px 8px',
                    borderRadius: 12,
                  }}
                >
                  {canEdit ? 'Can edit' : 'View only'}
                </span>
              </div>
            </>
          ) : (
            renewalDate && (
              <div
                style={{
                  fontSize: 13,
                  color: '#6B7280',
                  marginTop: 2,
                }}
              >
                {'Renewal: '}
                {renewalDisplay}
              </div>
            )
          )}
        </div>

        {/* Right side: Sharing icon + Menu */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexShrink: 0,
          }}
        >
          {/* Sharing indicator icon */}
          {sharingIconColor && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSharingIconClick?.();
              }}
              style={{
                width: 32,
                height: 32,
                color: sharingIconColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: onSharingIconClick ? 'pointer' : 'default',
                borderRadius: 8,
                padding: 4,
                transition: 'background 0.2s',
              }}
              title={(() => {
                if (isSharedToMe) return 'Shared with you - tap for details';
                if (isSharedViaParent) return `Shared with ${parentSharesCount} ${parentSharesCount === 1 ? 'person' : 'people'} - tap for details`;
                if (isSharedViaShareAll) return 'Shared via Share All - tap for details';
                return `Shared with ${sharesCount} ${sharesCount === 1 ? 'person' : 'people'} - tap for details`;
              })()}
            >
              <Sharing />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentCard;
